import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, X, ChevronLeft, ChevronRight, Download, Calendar, User, 
  MapPin, RefreshCw, Filter, Grid, List, Camera, Clock, SplitSquareVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { uploadsAPI, bookingsAPI } from '../../services/api';
import BeforeAfterComparison from './BeforeAfterComparison';

const PhotoGalleryTab = () => {
  const [photos, setPhotos] = useState([]);
  const [bookings, setBookings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [comparisonBooking, setComparisonBooking] = useState(null); // For before/after comparison
  const [filter, setFilter] = useState({
    photoType: '', // before, during, after
    bookingId: ''
  });
  const [groupedPhotos, setGroupedPhotos] = useState({});

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const params = { upload_type: 'worker_job_photo' };
      const response = await uploadsAPI.getAllUploads(params);
      const photoData = response.data.uploads || [];
      setPhotos(photoData);
      
      // Group photos by booking
      const grouped = {};
      for (const photo of photoData) {
        const bookingId = photo.booking_id || 'unassigned';
        if (!grouped[bookingId]) {
          grouped[bookingId] = {
            before: [],
            during: [],
            after: [],
            other: []
          };
        }
        const type = photo.photo_type || 'other';
        if (grouped[bookingId][type]) {
          grouped[bookingId][type].push(photo);
        } else {
          grouped[bookingId].other.push(photo);
        }
      }
      setGroupedPhotos(grouped);
      
      // Fetch booking details for each unique booking
      const uniqueBookingIds = [...new Set(photoData.map(p => p.booking_id).filter(Boolean))];
      const bookingDetails = {};
      for (const bid of uniqueBookingIds) {
        try {
          const bookingRes = await bookingsAPI.getById(bid);
          bookingDetails[bid] = bookingRes.data;
        } catch (e) {
          bookingDetails[bid] = { id: bid, service_name: 'Unknown Service' };
        }
      }
      setBookings(bookingDetails);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const filteredGroupedPhotos = () => {
    let result = { ...groupedPhotos };
    
    if (filter.bookingId) {
      result = { [filter.bookingId]: result[filter.bookingId] };
    }
    
    return result;
  };

  const getPhotoTypeLabel = (type) => {
    const labels = {
      'before': 'Before',
      'during': 'In Progress',
      'after': 'After'
    };
    return labels[type] || type;
  };

  const getPhotoTypeBadgeColor = (type) => {
    const colors = {
      'before': 'bg-blue-500/20 text-blue-400',
      'during': 'bg-yellow-500/20 text-yellow-400',
      'after': 'bg-green-500/20 text-green-400'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  const navigatePhoto = (direction) => {
    if (!selectedPhoto) return;
    const allPhotos = photos.filter(p => 
      (!filter.bookingId || p.booking_id === filter.bookingId) &&
      (!filter.photoType || p.photo_type === filter.photoType)
    );
    const currentIndex = allPhotos.findIndex(p => p.id === selectedPhoto.id);
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allPhotos.length - 1;
    } else {
      newIndex = currentIndex < allPhotos.length - 1 ? currentIndex + 1 : 0;
    }
    setSelectedPhoto(allPhotos[newIndex]);
  };

  const totalPhotos = photos.length;
  const beforeCount = photos.filter(p => p.photo_type === 'before').length;
  const duringCount = photos.filter(p => p.photo_type === 'during').length;
  const afterCount = photos.filter(p => p.photo_type === 'after').length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="photo-gallery-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Job Photo Gallery</h1>
          <p className="text-[var(--text-muted)] mt-1">Before, during, and after photos from completed jobs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4" />
          </Button>
          <Button onClick={fetchPhotos} variant="outline" className="gap-2 ml-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-4 text-center">
          <Camera className="w-6 h-6 mx-auto mb-2 text-[#D4AF37]" />
          <p className="text-2xl font-bold">{totalPhotos}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Photos</p>
        </div>
        <div className="card p-4 text-center cursor-pointer hover:bg-white/5" onClick={() => setFilter({ ...filter, photoType: 'before' })}>
          <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-blue-500/20 flex items-center justify-center">
            <span className="text-blue-400 text-xs font-bold">B</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">{beforeCount}</p>
          <p className="text-sm text-[var(--text-muted)]">Before Photos</p>
        </div>
        <div className="card p-4 text-center cursor-pointer hover:bg-white/5" onClick={() => setFilter({ ...filter, photoType: 'during' })}>
          <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <span className="text-yellow-400 text-xs font-bold">D</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{duringCount}</p>
          <p className="text-sm text-[var(--text-muted)]">During Photos</p>
        </div>
        <div className="card p-4 text-center cursor-pointer hover:bg-white/5" onClick={() => setFilter({ ...filter, photoType: 'after' })}>
          <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-green-500/20 flex items-center justify-center">
            <span className="text-green-400 text-xs font-bold">A</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{afterCount}</p>
          <p className="text-sm text-[var(--text-muted)]">After Photos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          value={filter.photoType}
          onChange={(e) => setFilter({ ...filter, photoType: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white"
        >
          <option value="">All Photo Types</option>
          <option value="before">Before Photos</option>
          <option value="during">During Photos</option>
          <option value="after">After Photos</option>
        </select>

        <select
          value={filter.bookingId}
          onChange={(e) => setFilter({ ...filter, bookingId: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white"
        >
          <option value="">All Bookings</option>
          {Object.keys(groupedPhotos).filter(id => id !== 'unassigned').map(bookingId => (
            <option key={bookingId} value={bookingId}>
              {bookings[bookingId]?.service_name || 'Booking'} - {bookingId.substring(0, 8)}...
            </option>
          ))}
        </select>

        {(filter.photoType || filter.bookingId) && (
          <Button 
            variant="ghost" 
            onClick={() => setFilter({ photoType: '', bookingId: '' })}
            className="text-[var(--text-muted)]"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Photo Gallery */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#D4AF37]" />
          <p className="mt-4 text-[var(--text-muted)]">Loading photos...</p>
        </div>
      ) : Object.keys(filteredGroupedPhotos()).length === 0 ? (
        <div className="text-center py-12 card">
          <Camera className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
          <p className="text-[var(--text-muted)]">No job photos found</p>
          <p className="text-sm text-[var(--text-muted)] mt-2">Photos uploaded during job completion will appear here</p>
        </div>
      ) : viewMode === 'grid' ? (
        // Grid View - Grouped by Booking
        <div className="space-y-8">
          {Object.entries(filteredGroupedPhotos()).map(([bookingId, photosByType]) => {
            const booking = bookings[bookingId];
            const allBookingPhotos = [
              ...photosByType.before,
              ...photosByType.during,
              ...photosByType.after,
              ...photosByType.other
            ].filter(p => !filter.photoType || p.photo_type === filter.photoType);
            
            if (allBookingPhotos.length === 0) return null;
            
            return (
              <div key={bookingId} className="card p-6">
                {/* Booking Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                  <div>
                    <h3 className="text-lg font-semibold">
                      {booking?.service_name || 'Job Photos'}
                    </h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-muted)]">
                      {booking?.scheduled_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(booking.scheduled_date).toLocaleDateString()}
                        </span>
                      )}
                      {booking?.address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {booking.address}
                        </span>
                      )}
                      {booking?.worker_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {booking.worker_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Before/After Comparison Button */}
                    {photosByType.before.length > 0 && photosByType.after.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => setComparisonBooking({
                          ...booking,
                          id: bookingId,
                          beforePhotos: photosByType.before,
                          afterPhotos: photosByType.after
                        })}
                        className="bg-[#D4AF37] text-black hover:bg-[#C4A030] gap-2"
                        data-testid={`compare-${bookingId}`}
                      >
                        <SplitSquareVertical className="w-4 h-4" />
                        Compare
                      </Button>
                    )}
                    <span className="text-sm text-[var(--text-muted)]">
                      {allBookingPhotos.length} photos
                    </span>
                  </div>
                </div>

                {/* Photo Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {allBookingPhotos.map((photo) => (
                    <motion.div
                      key={photo.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      <img
                        src={photo.file_url}
                        alt={photo.description || 'Job photo'}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPhotoTypeBadgeColor(photo.photo_type)}`}>
                          {getPhotoTypeLabel(photo.photo_type)}
                        </span>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs truncate">{photo.uploader_name}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // List View
        <div className="space-y-4">
          {photos
            .filter(p => (!filter.photoType || p.photo_type === filter.photoType) && (!filter.bookingId || p.booking_id === filter.bookingId))
            .map((photo) => {
              const booking = bookings[photo.booking_id];
              return (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="card p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={photo.file_url}
                      alt={photo.description || 'Job photo'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPhotoTypeBadgeColor(photo.photo_type)}`}>
                        {getPhotoTypeLabel(photo.photo_type)}
                      </span>
                      <span className="text-sm text-[var(--text-muted)]">
                        {booking?.service_name || 'Unknown Service'}
                      </span>
                    </div>
                    <p className="text-sm text-white truncate">
                      {photo.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {photo.uploader_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(photo.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
            onClick={() => setSelectedPhoto(null)}
          >
            <Button
              className="absolute top-4 right-4 z-10"
              variant="ghost"
              onClick={() => setSelectedPhoto(null)}
            >
              <X className="w-6 h-6" />
            </Button>

            <Button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); navigatePhoto('prev'); }}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>

            <Button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); navigatePhoto('next'); }}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>

            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-5xl max-h-[85vh] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedPhoto.file_url}
                alt={selectedPhoto.description || 'Job photo'}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              
              {/* Photo Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPhotoTypeBadgeColor(selectedPhoto.photo_type)}`}>
                    {getPhotoTypeLabel(selectedPhoto.photo_type)}
                  </span>
                  {bookings[selectedPhoto.booking_id] && (
                    <span className="text-white font-medium">
                      {bookings[selectedPhoto.booking_id].service_name}
                    </span>
                  )}
                </div>
                {selectedPhoto.description && (
                  <p className="text-white/80 mb-2">{selectedPhoto.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-white/60">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {selectedPhoto.uploader_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(selectedPhoto.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Before/After Comparison Modal */}
      <AnimatePresence>
        {comparisonBooking && (
          <BeforeAfterComparison
            beforePhotos={comparisonBooking.beforePhotos}
            afterPhotos={comparisonBooking.afterPhotos}
            booking={comparisonBooking}
            onClose={() => setComparisonBooking(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PhotoGalleryTab;
